import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';

import { Button } from '@/components/Button';
import { TextField } from '@/components/TextField';
import { PasswordInput } from '@/components/PasswordInput';
import { signIn, signOut, AuthActionError } from '@/lib/auth-actions';
import { signInSchema, type SignInValues } from '@/lib/auth-schemas';
import { getProfile } from '@/lib/db/profile';
import { getSupabaseClient } from '@/lib/supabase';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';
import { useRecaptchaToken } from '@/lib/useRecaptchaToken';
import { verifyRecaptcha } from '@/lib/recaptcha';
import { env } from '@/lib/env';

// Back-office signin with explicit role gate. The signIn() helper
// authenticates against Supabase, then we refetch the profile and reject
// anything that isn't an active admin / super_admin: a buyer who somehow
// lands on /admin/login or a disabled admin must NOT be allowed to
// bypass RequireRole through this entry point. RLS on /admin/* would
// still 403 the page, but rejecting at submit-time gives a clean toast
// instead of a sudden 403 redirect.
//
// Hardening (auth cross-entrypoint, 2026-05-05):
//  - useAuthRedirect bounces an already-signed-in user away from the form
//    (admins → /admin, buyers → /dashboard, no loop).
//  - reCAPTCHA v3 with action='admin_signin' protects the endpoint with
//    a stricter rate-limit pool than buyer signin (DB CHECK + helper
//    branch — migration 20260505000000).
export function AdminLoginPage() {
  const { t } = useTranslation(['auth']);
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  // Bounce an already-signed-in user away from the form. fallback='/admin'
  // is the canonical post-signin destination for back-office users; if a
  // buyer with a stale session navigates here, /admin's RequireRole will
  // 403 with a contextual CTA back to /dashboard (Page403 hardening,
  // 2026-05-05). `enabled: !submitting` suppresses the redirect during
  // the role-gate below — see SignInPage for the full rationale.
  useAuthRedirect({ mode: 'requireSignedOut', fallback: '/admin', enabled: !submitting });

  const getRecaptchaToken = useRecaptchaToken();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<SignInValues>({
    resolver: zodResolver(signInSchema(t)),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true);
    try {
      // reCAPTCHA v3 with the admin-only action. Same dev-bypass / verify
      // contract as buyer signin; the action name keeps admin attempts on
      // a separate rate-limit and audit trail (migration 20260505000000).
      const token = await getRecaptchaToken('admin_signin');
      if (!token && env.recaptchaSiteKey) throw new AuthActionError('recaptcha_failed');
      if (token) {
        const verdict = await verifyRecaptcha({ token, action: 'admin_signin' });
        if (!verdict.success) {
          throw new AuthActionError(
            verdict.code === 'rate_limited' ? 'recaptcha_rate_limited' : 'recaptcha_failed',
          );
        }
      }

      await signIn(values);

      const client = getSupabaseClient();
      const { data: sessionData } = await client.auth.getSession();
      const userId = sessionData.session?.user.id;
      if (!userId) {
        toast.error(t('auth:errors.generic'));
        return;
      }

      const profile = await getProfile(userId, client);
      const isAdmin = profile?.role === 'admin' || profile?.role === 'super_admin';
      const isActive = profile?.status === 'active' && profile?.deleted_at === null;

      if (!profile || !isAdmin || !isActive) {
        await signOut(client).catch(() => undefined);
        toast.error(t('auth:errors.notAdmin'));
        return;
      }

      navigate('/admin', { replace: true });
    } catch (error) {
      if (error instanceof AuthActionError) {
        if (error.code === 'recaptcha_failed') {
          toast.error(t('auth:errors.recaptchaFailed'));
        } else if (error.code === 'recaptcha_rate_limited') {
          toast.error(t('auth:errors.recaptchaRateLimited'));
        } else if (error.code === 'invalid_credentials') {
          setError('password', { message: t('auth:errors.invalidCredentials') });
        } else if (error.code === 'rate_limited') {
          toast.error(t('auth:errors.rateLimited'));
        } else if (error.code === 'network') {
          toast.error(t('auth:errors.network'));
        } else {
          toast.error(t('auth:errors.generic'));
        }
      } else {
        toast.error(t('auth:errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <section className="m5-shell">
      <h1 className="m5-shell__title">{t('auth:adminLogin.title')}</h1>
      <p className="m5-shell__lead">{t('auth:adminLogin.lead')}</p>

      <div className="m5-card">
        <form className="m5-form" onSubmit={onSubmit} noValidate>
          <TextField
            label={t('auth:fields.email')}
            type="email"
            autoComplete="email"
            inputMode="email"
            required
            error={errors.email?.message}
            {...register('email')}
          />
          <PasswordInput
            label={t('auth:fields.password')}
            autoComplete="current-password"
            required
            error={errors.password?.message}
            showLabel={t('auth:common.passwordShow')}
            hideLabel={t('auth:common.passwordHide')}
            {...register('password')}
          />
          <Button
            type="submit"
            variant="primary"
            loading={submitting}
            loadingLabel={t('auth:common.loading')}
          >
            {t('auth:adminLogin.submit')}
          </Button>
        </form>

        <p className="m5-footnote">{t('auth:adminLogin.footnote')}</p>
      </div>
    </section>
  );
}
